import {
  minimumHeaderBand,
  type PatientField,
  PRINT_HEADER_LAYOUT_LABELS,
  type PrintHeader,
  type PrintHeaderLayout,
  printHeaderLayoutSchema,
  type PrintTemplateDocument,
} from '#shared/types';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { LinesField } from '../controls/LinesField';
import { LogoField } from '../controls/LogoField';
import { NumberField } from '../controls/NumberField';
import { OrderedList } from '../controls/OrderedList';

interface HeaderSectionProps {
  /** The whole document: the band's minimum depends on the page and the theme too. */
  document: PrintTemplateDocument;
  onChange: (patch: Partial<PrintHeader>) => void;
}

interface HeaderFieldsProps {
  header: PrintHeader;
  onChange: HeaderSectionProps['onChange'];
}

export function HeaderSection({ document, onChange }: HeaderSectionProps) {
  const header = document.header;

  return (
    <>
      <Checkbox
        label="Print a letterhead"
        checked={header.enabled}
        onChange={(event) => onChange({ enabled: event.target.checked })}
      />

      {!header.enabled && (
        <NumberField
          label="Blank space at the top"
          unit="px"
          value={header.blank_space}
          onChange={(blank_space) => onChange({ blank_space })}
          min={0}
          max={800}
          step={1}
          helpText="For paper with a letterhead already printed on it: the report starts below this much clear space."
        />
      )}

      {header.enabled && (
        <>
          <ProfileFields header={header} onChange={onChange} />

          <ArrangementFields document={document} onChange={onChange} />

          <MarkFields header={header} onChange={onChange} />

          <LabNameFields header={header} onChange={onChange} />

          <RuleFields header={header} onChange={onChange} />

          <BarcodeControls header={header} onChange={onChange} />
          <PatientFields header={header} onChange={onChange} />
        </>
      )}
    </>
  );
}

function BarcodeControls({
  header,
  onChange,
}: {
  header: PrintHeader;
  onChange: HeaderSectionProps['onChange'];
}) {
  const barcode = header.barcode;
  const patch = (next: Partial<PrintHeader['barcode']>) =>
    onChange({ barcode: { ...barcode, ...next } });

  return (
    <>
      <Checkbox
        label="Patient barcode"
        checked={barcode.enabled}
        onChange={(event) => patch({ enabled: event.target.checked })}
      />

      {barcode.enabled && (
        <>
          <Select
            label="Encodes"
            value={barcode.source}
            onChange={(choice) => patch({ source: choice })}
            options={[
              { value: 'pin_no', label: 'PIN number' },
              { value: 'mr_no', label: 'MR number' },
            ]}
            helpText="A patient with no MR number simply gets no barcode."
          />
          <div className="c-builder__grid">
            <NumberField
              label="Bar height"
              unit="mm"
              value={barcode.height}
              onChange={(height) => patch({ height })}
              min={4}
              max={40}
            />
            <NumberField
              label="Bar width"
              unit="mm"
              value={barcode.module_width}
              onChange={(module_width) => patch({ module_width })}
              min={0.25}
              max={0.6}
              step={0.01}
              helpText="The narrowest bar. Below 0.25 mm most office printers stop producing a readable symbol."
            />
          </div>
          <Checkbox
            label="Print the value under the bars"
            checked={barcode.show_value}
            onChange={(event) => patch({ show_value: event.target.checked })}
          />
        </>
      )}
    </>
  );
}

function PatientFields({
  header,
  onChange,
}: {
  header: PrintHeader;
  onChange: HeaderSectionProps['onChange'];
}) {
  return (
    <>
      <NumberField
        label="Detail columns"
        value={header.patient_columns}
        onChange={(patient_columns) => onChange({ patient_columns })}
        min={1}
        max={4}
        helpText="Fields fill the first column top to bottom, then the next."
      />

      <OrderedList<PatientField>
        items={header.patient_fields}
        onChange={(patient_fields) => onChange({ patient_fields })}
        keyOf={(field) => field.key}
        labelOf={(field) => field.label || field.key}
        enabledOf={(field) => field.enabled}
        setEnabled={(field, enabled) => ({ ...field, enabled })}
        detail={(field, replace) => (
          <TextField
            label={`Printed label for ${field.key}`}
            hideLabel
            value={field.label}
            placeholder="Printed label"
            onChange={(event) => replace({ ...field, label: event.target.value })}
          />
        )}
      />
    </>
  );
}

/** Whether the letterhead reads the lab's name and mark from Company details. */
function ProfileFields({ header, onChange }: HeaderFieldsProps) {
  return (
    <>
      {/*
        Off by default on every template, existing and new. A stored
        document is re-parsed on the way out of the database, so defaulting
        this on would have rewritten every lab's letterhead on deploy — and
        the built-in NDC sheet deliberately leaves the name blank because
        the mark image already carries it.
      */}
      <Checkbox
        label="Use the company details"
        checked={header.use_lab_profile}
        onChange={(event) => onChange({ use_lab_profile: event.target.checked })}
      />
      {header.use_lab_profile && (
        <p className="c-field__help">
          The lab name, the lines beneath it and the mark come from Company details. The three
          fields below are ignored while this is on.
        </p>
      )}
    </>
  );
}

/** Layout, which pages carry it, and the band it is drawn in. */
function ArrangementFields({ document, onChange }: HeaderSectionProps) {
  const header = document.header;
  // The same function the renderer uses to guarantee nothing is clipped. Shown
  // rather than silently applied, so the number in the input is not a lie.
  const minimum = Math.ceil(minimumHeaderBand(document) * 10) / 10;
  const short = header.band < minimum;

  return (
    <>
      <Select
        label="Arrangement"
        value={header.layout}
        onChange={(choice) => onChange({ layout: choice })}
        options={printHeaderLayoutSchema.options.map((layout: PrintHeaderLayout) => ({
          value: layout,
          label: PRINT_HEADER_LAYOUT_LABELS[layout],
        }))}
      />

      <Select
        label="Print it on"
        value={header.repeat}
        onChange={(choice) => onChange({ repeat: choice === 'first' ? 'first' : 'every' })}
        options={[
          { value: 'every', label: 'Every page' },
          { value: 'first', label: 'The first page, then a name-and-PIN strip' },
        ]}
      />

      <NumberField
        label="Band height"
        unit="mm"
        value={header.band}
        onChange={(band) => onChange({ band })}
        min={0}
        max={120}
        step={1}
        helpText={
          short
            ? `Your letterhead needs ${minimum} mm, so ${minimum} mm is what will be used. A PDF fixes its margins for the whole document, so the band cannot grow on the page that needs it.`
            : `Your letterhead needs at least ${minimum} mm.`
        }
      />
    </>
  );
}

/** The mark image and its size. */
function MarkFields({ header, onChange }: HeaderFieldsProps) {
  return (
    <>
      <LogoField
        logo={header.logo}
        onChange={(patch) => onChange({ logo: { ...header.logo, ...patch } })}
      />

      {header.logo.enabled && (
        <div className="c-builder__grid">
          <NumberField
            label="Mark width"
            unit="mm"
            value={header.logo.width}
            onChange={(width) => onChange({ logo: { ...header.logo, width } })}
            min={5}
            max={120}
          />
          <NumberField
            label="Mark height"
            unit="mm"
            value={header.logo.height}
            onChange={(height) => onChange({ logo: { ...header.logo, height } })}
            min={5}
            max={120}
          />
        </div>
      )}
    </>
  );
}

/** The lab name and the lines under it. */
function LabNameFields({ header, onChange }: HeaderFieldsProps) {
  return (
    <>
      <TextField
        label="Lab name"
        value={header.lab_name}
        placeholder="Leave blank when the mark already carries it"
        onChange={(event) => onChange({ lab_name: event.target.value })}
      />

      <LinesField
        label="Address and contact lines"
        lines={header.lab_lines}
        max={4}
        onChange={(lab_lines) => onChange({ lab_lines })}
        helpText="Printed small, under the lab name."
      />
    </>
  );
}

/** The rule under the letterhead. */
function RuleFields({ header, onChange }: HeaderFieldsProps) {
  return (
    <>
      <Checkbox
        label="Rule under the letterhead"
        checked={header.rule}
        onChange={(event) => onChange({ rule: event.target.checked })}
      />
      {header.rule && (
        <NumberField
          label="Second-accent segment"
          unit="mm"
          value={header.rule_accent_width}
          onChange={(rule_accent_width) => onChange({ rule_accent_width })}
          min={0}
          max={200}
          helpText="How far the short leading colour runs before the rule changes."
        />
      )}
    </>
  );
}
